/**
 * This file is a part of the Promvc framework.
 * 
 * (c) ThemePlexi
 * 
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import $ from 'jquery';

/**
 * Declare private properties
 * @private
 */

const NAME = 'theme-switch';
const VERSION = '1.0.0';
const DATA_KEY = 'promvc.theme-switch';

const CLASS_NAME_BUTTON_THEME_ICON = 'c-nav__link__icon--theme';
const SELECTOR_DATA_TOGGLE_THEME = '[data-toggle="theme-switch"]';

let initialised = false,
	dark = 'dark', light = 'light',
	themeKey = 'theme';

/**
 * The theme switch helper.
 * 
 * @since 1.0.0
 */
class ThemeSwitch
{
	/**
	 * The constructor
	 */
	constructor()
	{
		this._container = document.documentElement;
		this._themeBtnIcon = document.querySelector( `.${CLASS_NAME_BUTTON_THEME_ICON}` );
		this._triggers = document.querySelectorAll( SELECTOR_DATA_TOGGLE_THEME );

		// Register params
		this._params = {
			currentTheme: '',
			themes: {
				[ light ]: 'white',
				[ dark ]: 'bluegrey'
			}
		};
	}

	// Getters

	static get VERSION() {
		return VERSION;
	}

	// protected

	/**
	 * Register theme switching events
	 * 
	 * @access protected
	 * @return {void}
	 */
	_registerThemeSwitch()
	{
		let mqScheme = ( window.matchMedia !== undefined ? window.matchMedia( '(prefers-color-scheme: dark)' ) : false ),
			localScheme = localStorage.getItem( themeKey );

		// assign current active theme
		if ( localScheme !== null ) {
			this._params.currentTheme = localScheme;
		} else if ( this.getSystemTheme() === dark ) {
			this._params.currentTheme = dark;
		} else {
			this._params.currentTheme = light;
		}

		// attach click event listeners
		if ( this._triggers.length > 0 )
		{
			for ( let i = 0; i < this._triggers.length; ++i ) {
				this._triggers[ i ].addEventListener( 'click', this.switchTheme.bind( this ) );
			}
		}

		// attach system event listeners
		if ( mqScheme.onchange !== undefined ) {
			mqScheme.onchange = this.switchTheme.bind( this );
		} else if ( Object.getPrototypeOf( mqScheme ) instanceof EventTarget ) { // access [[prototype]] property
			mqScheme.addEventListener( 'change', this.switchTheme.bind( this ) );
		} else { // use for backwards compatibility in older browsers
			mqScheme.addListener( this.switchTheme.bind( this ) );
		}
	}

	// public

	/**
	 * Get the set system theme color setting.
	 * 
	 * @access public
	 * @return {String}
	 */
	getSystemTheme()
	{
		const curr = window.getComputedStyle( document.documentElement ).getPropertyValue( 'content' ).replace( /"/g, '' ).trim();
		return ( curr === 'normal' ? light : dark );
	}

	/**
	 * Initialize.
	 * 
	 * @access public
	 * @return {void}
	 */
	init()
	{
		if ( initialised ) return;

		// Initialize
		this.switchTheme();

		// Register events
		this._registerThemeSwitch();

		// register init
		initialised = true;
	}

	/**
	 * Switch current theme.
	 * 
	 * @access public
	 * @return {void}
	 */
	switchTheme( e )
	{
		if ( e && e.type === 'change' )
		{
			if ( e.matches ) {
				this.toggleSwitch( 'os-' + dark );
			} else {
				this.toggleSwitch( 'os-' + light );
			}
		}
		else if ( e && e.type === 'click' )
		{
			const el = e.currentTarget;

			e.preventDefault();
			e.stopPropagation();

			this.toggleSwitch( el.getAttribute( 'data-theme' ) );
		}
		else
		{
			const theme = localStorage.getItem( themeKey );
			if ( theme !== null ) {
				this.toggleSwitch( theme );
			}
		}
	}

	/**
	 * Handle switching.
	 * 
	 * @access public
	 * @return {void}
	 */
	toggleSwitch( theme )
	{
		let containerClasses = [].slice.call( this._container.classList ),
			themeBtnIconClasses = [].slice.call( this._themeBtnIcon.classList ),
			currThemeClass = '', currIconClass = '', currTheme = theme || light,
			match = currTheme.match( /^os[-]*(\w+)?/ ), cls;

		for ( cls of containerClasses )
		{
			if ( /t-[\w]+/.test( cls ) ) {
				currThemeClass = cls;
				break;
			}
		}

		for ( cls of themeBtnIconClasses )
		{
			if ( /[\w]+-theme-[\w]+/.test( cls ) ) {
				currIconClass = cls;
				break;
			}
		}

		// normalize theme
		if ( match )
		{
			theme = 'os';

			if ( match[ 1 ] ) {
				currTheme = match[ 1 ];
			} else {
				currTheme = this.getSystemTheme();
			}
		}
		else if ( currTheme !== light && currTheme !== dark )
		{
			currTheme = light;
			theme = light;
		}

		// change dom elements
		this._container.classList.replace( currThemeClass, 't-' + this._params.themes[ currTheme ] );
		this._themeBtnIcon.classList.replace( currIconClass, 'icon-theme-' + theme );

		// store current theme
		localStorage.setItem( themeKey, theme );
	}

	// Static

	static _jQueryInterface( element, config )
	{
		const $element = $( element );
		let data = $element.data( DATA_KEY );

		if ( ! data ) {
			data = new ThemeSwitch();
			$element.data( DATA_KEY, data );
		}

		if ( typeof config === 'string' )
		{
			if ( typeof data[ config ] === 'undefined' ) {
				throw new TypeError( `No method named "${config}"` );
			}

			data[ config ]();
		}
	}
}

/**
 * Module exports
 */
export default ThemeSwitch;
